'use client';

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LIST_BADGE_LIFECYCLE_STATE } from '@terra/shared-dto-types';
import type {
  AssetsSearchMetricsBodyDto,
  AssetsSearchMetricsReturnDto,
} from '@terra/shared-contracts-assets';
import { terraProxyApiClient } from '../clients/testerClient';
import { useAppTargetId } from '../store/projectStore';

export const ASSET_COUNTS_QUERY_KEY = 'asset-counts';

const EMPTY_ASSET_COUNTS = {
  endpointCount: 0,
  webPageCount: 0,
  agentCount: 0,
  unverifiedCount: 0,
  totalCount: 0,
} as const;

export function useAssetCounts() {
  const appTargetId = useAppTargetId();

  const { data, isLoading } = useQuery({
    queryKey: [ASSET_COUNTS_QUERY_KEY, appTargetId, LIST_BADGE_LIFECYCLE_STATE],
    queryFn: async () => {
      if (!appTargetId) {
        return EMPTY_ASSET_COUNTS;
      }

      const result = await terraProxyApiClient.countApplicationEntities({
        applicationId: appTargetId,
        kind: 'assets',
        mode: 'typeCounts',
        lifecycleState: LIST_BADGE_LIFECYCLE_STATE,
      });

      const typeCounts = result?.typeCounts;

      return {
        endpointCount: typeCounts?.endpoints ?? 0,
        webPageCount: typeCounts?.webpages ?? 0,
        agentCount: typeCounts?.agents ?? 0,
        unverifiedCount: typeCounts?.unverified ?? 0,
        totalCount: result?.total ?? 0,
      };
    },
    enabled: !!appTargetId,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  return {
    endpointCount: data?.endpointCount ?? 0,
    webPageCount: data?.webPageCount ?? 0,
    agentCount: data?.agentCount ?? 0,
    unverifiedCount: data?.unverifiedCount ?? 0,
    totalCount: data?.totalCount ?? 0,
    isLoading,
  };
}

export function useAssetsSegmentMetrics({
  body,
}: {
  body: AssetsSearchMetricsBodyDto | null;
}): AssetsSearchMetricsReturnDto | null {
  const appTargetId = useAppTargetId();

  const { data } = useQuery({
    queryKey: [ASSET_COUNTS_QUERY_KEY, appTargetId, 'metrics', body],
    queryFn: async () => {
      if (!appTargetId || !body) {
        return null;
      }

      const result = await terraProxyApiClient.getApplicationAssetsMetrics({
        applicationId: appTargetId,
        body,
      });

      return result ?? null;
    },
    enabled: !!body && !!appTargetId,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  return data ?? null;
}

export const useInvalidateAssetCounts = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async ({ applicationId }: { applicationId: string }) => {
      await queryClient.invalidateQueries({
        queryKey: [ASSET_COUNTS_QUERY_KEY, applicationId],
        exact: false,
      });
    },
    [queryClient],
  );
};
